import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PhoneCall } from 'lucide-react';
import { Reveal, Words } from '../motion/Reveal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

const WHY_IMG = 'https://images.unsplash.com/photo-1626266061368-46a8f578ddd6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';

const IconCards = ({ items, testid }) => (
  <div className="grid md:grid-cols-3 gap-5" data-testid={testid}>
    {items.map(([Icon, t, d], i) => (
      <Reveal key={t} delay={i * 150} className="group bg-white border border-emerald-100 rounded-2xl p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
        <div className="w-12 h-12 rounded-xl bg-emerald-50 text-[#1a5c3b] flex items-center justify-center mb-4 group-hover:bg-[#1a5c3b] group-hover:text-white transition-colors"><Icon className="w-6 h-6" /></div>
        <h3 className="font-bold text-lg mb-2">{t}</h3>
        <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
      </Reveal>
    ))}
  </div>
);

export const WhyUs = ({ c }) => (
  <section className="max-w-6xl mx-auto px-4 py-16 md:py-24 overflow-hidden grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-testid="calc-why">
    <Reveal className="relative">
      <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-[#1a5c3b] to-emerald-400 -rotate-2" />
      <img src={WHY_IMG} alt="" loading="lazy" className="relative w-full h-72 md:h-96 object-cover rounded-[1.75rem] shadow-xl" />
    </Reveal>
    <div>
      <Words parts={[[c.whyTitle]]} className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-tight mb-8" />
      <div className="space-y-6">
        {c.why.map(([Icon, t, d], i) => (
          <Reveal key={t} delay={150 + i * 150} className="flex gap-4">
            <div className="w-12 h-12 rounded-full bg-[#1a5c3b] text-white flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
            <div><p className="font-bold text-lg">{t}</p><p className="text-slate-600">{d}</p></div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Guides = ({ c }) => (
  <section className="bg-[#f8faf7] py-16 md:py-24" data-testid="calc-guides">
    <div className="max-w-6xl mx-auto px-4 space-y-20">
      <div className="max-w-3xl">
        <Words parts={[[c.simTitle]]} className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-tight mb-4" />
        <Reveal as="p" delay={150} className="text-slate-600 text-lg leading-relaxed">{c.simText}</Reveal>
      </div>
      <div>
        <Words parts={[[c.howTitle]]} className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-tight mb-3" />
        <Reveal as="p" delay={150} className="text-slate-600 text-lg mb-8 max-w-3xl">{c.howIntro}</Reveal>
        <IconCards items={c.how} testid="calc-how" />
        <p className="mt-6 text-slate-600">{c.doubt[0]}<Link to="/contact" className="text-[#1a5c3b] font-semibold underline underline-offset-4">{c.doubt[1]}</Link>{c.doubt[2]}</p>
      </div>
      <div>
        <Words parts={[[c.tipsTitle]]} className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-tight mb-3" />
        <Reveal as="p" delay={150} className="text-slate-600 text-lg mb-8 max-w-3xl">{c.tipsIntro}</Reveal>
        <IconCards items={c.tips} testid="calc-tips" />
      </div>
    </div>
  </section>
);

export const CalcFaq = ({ c }) => (
  <section className="max-w-3xl mx-auto px-4 py-16 md:py-24" data-testid="calc-faq">
    <Words parts={[[c.faqTitle]]} className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight mb-8 text-center" />
    <Accordion type="single" collapsible className="space-y-3">
      {c.faq.map(([q, a], i) => (
        <Reveal key={q} delay={i * 100}>
        <AccordionItem value={`q${i}`} className="bg-white border border-emerald-100 rounded-2xl px-5 shadow-sm">
          <AccordionTrigger className="text-left font-semibold text-base hover:no-underline" data-testid={`calc-faq-q${i + 1}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-slate-600 leading-relaxed">{a}</AccordionContent>
        </AccordionItem>
        </Reveal>
      ))}
    </Accordion>
  </section>
);

export const CalcCta = ({ c }) => (
  <section className="max-w-6xl mx-auto px-4 pb-20" data-testid="calc-cta">
    <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#1a5c3b] to-[#0f3d26] px-8 py-12 md:px-14 md:py-16 text-center">
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff33_1px,transparent_1px)] [background-size:20px_20px]" />
      <div className="relative">
        <Words parts={[[c.ctaTitle]]} className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight !text-white mb-4" />
        <Reveal as="p" delay={250} className="text-emerald-100 text-base md:text-lg max-w-2xl mx-auto mb-8">{c.ctaText}</Reveal>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/verifier-admissibilite" className="inline-flex items-center justify-center gap-2 bg-white text-[#1a5c3b] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-lg" data-testid="calc-cta-prequal">
            {c.prequal}<ArrowRight className="w-5 h-5" />
          </Link>
          <a href="tel:5144183968" className="inline-flex items-center justify-center gap-2 border-2 border-white/60 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-colors" data-testid="calc-cta-call">
            <PhoneCall className="w-5 h-5" />514-418-3968
          </a>
        </div>
      </div>
    </div>
  </section>
);
